import { useEffect, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { usersAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Select, Card, Pagination, useToast, Input, Spinner } from '../components/UI';

// ── Constants ─────────────────────────────────────────────────────────────────
const ROLE_TABS = ['All', 'Search'];

const EMPTY_FILTERS = { name: '', email: '', country: '', state: '', city: '', area: '', mentor: '', status: '', roles: [], noRole: false };

const ROLE_COLORS = {
  swalath:  { bg: '#ede9fe', border: '#c4b5fd', text: '#6d28d9' },
  chishthy: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  "bai'ath":{ bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
  silsila:  { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
};

function roleStyle(roleName) {
  return ROLE_COLORS[roleName?.toLowerCase()] || { bg: 'var(--surface2)', border: 'var(--border)', text: 'var(--text2)' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function Avatar({ name, size = 40 }) {
  const initials = name
    ? name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const colors = ['#2d6a4f', '#1a5276', '#7c3aed', '#b5862a', '#c0392b', '#0f6e56'];
  const color = colors[name?.charCodeAt(0) % colors.length] || colors[0];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color + '22', border: `1.5px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 600, color, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function MetaField({ label, value, wide = false }) {
  return (
    <div style={{ minWidth: wide ? '100%' : 160 }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--text)' }}>{value || '—'}</div>
    </div>
  );
}

function RolePill({ role }) {
  const s = roleStyle(role);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 99,
      background: s.bg, border: `1px solid ${s.border}`, color: s.text,
      fontSize: 11, fontWeight: 600, letterSpacing: '.04em',
    }}>{role}</span>
  );
}

function parseRoles(rolesField) {
  if (!rolesField) return [];
  if (Array.isArray(rolesField)) return rolesField.map(r => (typeof r === 'string' ? r : r.name)).filter(r => r !== 'Admin');
  return rolesField.split(', ').filter(r => r !== 'Admin');
}

// ── Excel export ──────────────────────────────────────────────────────────────
function buildExcel(allUsers, roles) {
  const wb = XLSX.utils.book_new();

  const toRows = (users) => users.map(u => ({
    Name:    u.name,
    Email:   u.email,
    Phone:   u.phone    || '',
    Gender:  u.gender   || '',
    Country: u.country  || '',
    State:   u.state    || '',
    City:    u.city     || '',
    Area:    u.area     || '',
    Mentor:  u.mentor   || '',
    Roles:   parseRoles(u.roles).join(', ') || 'No Role',
    Status:  u.status,
    Joined:  u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
  }));

  // Sheet 1 — All Users
  const allSheet = XLSX.utils.json_to_sheet(toRows(allUsers));
  styleSheet(allSheet, allUsers.length);
  XLSX.utils.book_append_sheet(wb, allSheet, 'All Users');

  // One sheet per role
  const roleNames = roles.filter(r => r.name !== 'Admin').map(r => r.name);
  for (const roleName of roleNames) {
    const filtered = allUsers.filter(u => parseRoles(u.roles).includes(roleName));
    const sheet = XLSX.utils.json_to_sheet(toRows(filtered));
    styleSheet(sheet, filtered.length);
    XLSX.utils.book_append_sheet(wb, sheet, roleName);
  }

  // Sheet — No Role
  const noRole = allUsers.filter(u => parseRoles(u.roles).length === 0);
  const noRoleSheet = XLSX.utils.json_to_sheet(toRows(noRole));
  styleSheet(noRoleSheet, noRole.length);
  XLSX.utils.book_append_sheet(wb, noRoleSheet, 'No Role');

  // Sheet — Summary
  const summary = [
    { Group: 'All Users', Count: allUsers.length },
    ...roleNames.map(r => ({ Group: r, Count: allUsers.filter(u => parseRoles(u.roles).includes(r)).length })),
    { Group: 'No Role', Count: noRole.length },
    { Group: '', Count: '' },
    { Group: 'Active',    Count: allUsers.filter(u => u.status === 'active').length },
    { Group: 'Pending',   Count: allUsers.filter(u => u.status === 'pending').length },
    { Group: 'Suspended', Count: allUsers.filter(u => u.status === 'suspended').length },
  ];
  const summarySheet = XLSX.utils.json_to_sheet(summary);
  summarySheet['!cols'] = [{ wch: 20 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `sulthaniya-users-${date}.xlsx`);
}

function styleSheet(ws) {
  ws['!cols'] = [{ wch: 28 }, { wch: 30 }, { wch: 16 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 28 }, { wch: 28 }, { wch: 24 }, { wch: 12 }, { wch: 16 }];
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Users() {
  const { show, ToastEl } = useToast();

  // Paginated "All" view state
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);
  const [meta, setMeta]                 = useState({});
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [activeRole, setActiveRole]     = useState('All');

  // Search tab state — all users fetched once, filtered client-side
  const [allUsers, setAllUsers]         = useState([]);
  const [allLoading, setAllLoading]     = useState(false);
  const [filters, setFilters]           = useState(EMPTY_FILTERS);

  // Shared
  const [roles, setRoles]               = useState([]);
  const [exporting, setExporting]       = useState(false);
  const [viewUser, setViewUser]         = useState(null);
  const [editUser, setEditUser]         = useState(null);
  const [selected, setSelected]         = useState(null);
  const [rolesModal, setRolesModal]     = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [saving, setSaving]             = useState(false);
  const [showAddUser, setShowAddUser]   = useState(false);

  // Load paginated users (used in "All" tab)
  const loadPaged = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await usersAPI.list({ page, limit: 15, search, status: statusFilter });
      setUsers(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load users', 'error'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  // Load all users for Search tab (fetched once, filtered client-side)
  const loadAllUsers = useCallback(async () => {
    setAllLoading(true);
    try {
      const { data } = await usersAPI.list({ page: 1, limit: 1000 });
      setAllUsers(data.data);
    } catch { show('Failed to load users', 'error'); }
    finally { setAllLoading(false); }
  }, []);

  useEffect(() => {
    if (activeRole === 'All') loadPaged();
  }, [activeRole, loadPaged]);

  useEffect(() => {
    if (activeRole === 'Search') loadAllUsers();
  }, [activeRole, loadAllUsers]);

  useEffect(() => {
    usersAPI.listRoles().then(r => setRoles(r.data.data));
  }, []);

  // Client-side filter for Search tab
  const filteredUsers = (() => {
    if (activeRole !== 'Search') return [];
    const f = filters;
    const q = (v, s) => !s || (v || '').toLowerCase().includes(s.toLowerCase().trim());
    return allUsers.filter(u => {
      if (!q(u.name,    f.name))    return false;
      if (!q(u.email,   f.email))   return false;
      if (!q(u.country, f.country)) return false;
      if (!q(u.state,   f.state))   return false;
      if (!q(u.city,    f.city))    return false;
      if (!q(u.area,    f.area))    return false;
      if (!q(u.mentor,  f.mentor))  return false;
      if (f.status && u.status !== f.status) return false;
      if (f.noRole || f.roles.length) {
        const userRoles = parseRoles(u.roles);
        if (f.noRole && f.roles.length) {
          // show users with no role OR any of the selected roles
          if (userRoles.length !== 0 && !f.roles.some(r => userRoles.includes(r))) return false;
        } else if (f.noRole) {
          if (userRoles.length !== 0) return false;
        } else {
          if (!f.roles.some(r => userRoles.includes(r))) return false;
        }
      }
      return true;
    });
  })();

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => {
    if (k === 'roles')  return v.length > 0;
    if (k === 'noRole') return v === true;
    return v !== '';
  });

  function setFilter(key, value) {
    setFilters(f => ({ ...f, [key]: value }));
  }

  function toggleRoleFilter(roleName) {
    setFilters(f => ({
      ...f,
      roles: f.roles.includes(roleName)
        ? f.roles.filter(r => r !== roleName)
        : [...f.roles, roleName],
    }));
  }

  function handleTabChange(tab) {
    setActiveRole(tab);
    setSearch('');
    setStatusFilter('');
    setPage(1);
    if (tab === 'Search') setFilters(EMPTY_FILTERS);
  }

  function reload() {
    if (activeRole === 'Search') loadAllUsers();
    else loadPaged();
  }

  async function updateStatus(id, status) {
    try {
      await usersAPI.updateStatus(id, status);
      show(`User ${status} successfully`);
      reload();
      setSelected(null);
      if (viewUser?.id === id) setViewUser(v => v ? { ...v, status } : v);
    } catch { show('Failed to update status', 'error'); }
  }

  async function saveRoles() {
    setSaving(true);
    try {
      await usersAPI.updateRoles(rolesModal.id, selectedRoles);
      show('Roles updated successfully');
      reload();
      setRolesModal(null);
    } catch { show('Failed to update roles', 'error'); }
    finally { setSaving(false); }
  }

  function openRolesModal(user, e) {
    e?.stopPropagation();
    const currentRoles = user.roles
      ? parseRoles(user.roles).map(rname => roles.find(r => r.name === rname)?.id).filter(Boolean)
      : [];
    setSelectedRoles(currentRoles);
    setRolesModal(user);
  }

  function handleExport(usersToExport) {
    if (!usersToExport.length) { show('No results to export', 'error'); return; }
    setExporting(true);
    try {
      buildExcel(usersToExport, roles);
      show(`Exported ${usersToExport.length} users`);
    } catch { show('Export failed', 'error'); }
    finally { setExporting(false); }
  }

  const columns = [
    {
      key: 'name', label: 'Name', render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={v} size={34} />
          <div>
            <div style={{ fontWeight: 500 }}>{v}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{row.email}</div>
          </div>
        </div>
      ),
    },
    { key: 'phone', label: 'Phone', render: v => v || '—' },
    {
      key: 'roles', label: 'Roles', render: v => {
        const list = parseRoles(v);
        return list.length
          ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{list.map(r => <RolePill key={r} role={r} />)}</div>
          : <span style={{ color: 'var(--text3)', fontSize: 12 }}>No role</span>;
      },
    },
    { key: 'status',     label: 'Status', render: v => <Badge label={v} type={v} /> },
    { key: 'created_at', label: 'Joined',  render: v => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
    {
      key: 'actions', label: '', render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn size="sm" variant="outline" onClick={e => { e.stopPropagation(); setViewUser(row); }}>View</Btn>
          <Btn size="sm" variant="ghost"   onClick={e => { e.stopPropagation(); setEditUser(row); }}>Edit</Btn>
          <Btn size="sm" variant="ghost"   onClick={e => openRolesModal(row, e)}>Roles</Btn>
          <Btn size="sm" variant="ghost"   onClick={e => { e.stopPropagation(); setSelected(row); }}>Status</Btn>
        </div>
      ),
    },
  ];

  const isSearchTab = activeRole === 'Search';
  const tableData   = isSearchTab ? filteredUsers : users;
  const tableLoad   = isSearchTab ? allLoading : loading;

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      {ToastEl}

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700 }}>Users</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Manage foundation members and their access roles.</p>
        </div>
        <Btn variant="primary" onClick={() => setShowAddUser(true)}>+ Add user</Btn>
      </div>

      {/* ── Role Tabs ── */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {ROLE_TABS.map(tab => {
          const active = activeRole === tab;
          return (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: active ? 600 : 400,
                color: active ? 'var(--accent2)' : 'var(--text2)',
                borderBottom: active ? '2px solid var(--accent2)' : '2px solid transparent',
                marginBottom: -1, transition: 'all .15s',
                borderRadius: '4px 4px 0 0',
              }}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {/* ── All tab filters ── */}
      {activeRole === 'All' && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <Input
            placeholder="Search name or email…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 240 }}
          />
          <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ width: 140 }}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </Select>
        </div>
      )}

      {/* ── Search tab ── */}
      {isSearchTab && (
        <div style={{ marginBottom: 16 }}>
          <div style={{
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 'var(--r2)', padding: '16px 18px', marginBottom: 12,
          }}>
            {/* Row 1 — text fields */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10, marginBottom: 14 }}>
              <Input
                placeholder="Name"
                value={filters.name}
                onChange={e => setFilter('name', e.target.value)}
              />
              <Input
                placeholder="Email"
                value={filters.email}
                onChange={e => setFilter('email', e.target.value)}
              />
              <Input
                placeholder="Country"
                value={filters.country}
                onChange={e => setFilter('country', e.target.value)}
              />
              <Input
                placeholder="State / Province"
                value={filters.state}
                onChange={e => setFilter('state', e.target.value)}
              />
              <Input
                placeholder="City"
                value={filters.city}
                onChange={e => setFilter('city', e.target.value)}
              />
              <Input
                placeholder="Area / Address"
                value={filters.area}
                onChange={e => setFilter('area', e.target.value)}
              />
              <Input
                placeholder="Mentor"
                value={filters.mentor}
                onChange={e => setFilter('mentor', e.target.value)}
              />
            </div>

            {/* Row 2 — role chips + status select */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>
                Role
              </span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {roles.filter(r => r.name !== 'Admin').map(r => {
                  const active = filters.roles.includes(r.name);
                  const s = roleStyle(r.name);
                  return (
                    <button
                      key={r.id}
                      onClick={() => toggleRoleFilter(r.name)}
                      style={{
                        padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500,
                        border: `1px solid ${active ? s.border : 'var(--border)'}`,
                        background: active ? s.bg : 'transparent',
                        color: active ? s.text : 'var(--text2)',
                        cursor: 'pointer', transition: 'all .12s',
                      }}
                    >
                      {r.name}
                    </button>
                  );
                })}
                <button
                  onClick={() => setFilter('noRole', !filters.noRole)}
                  style={{
                    padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500,
                    border: `1px solid ${filters.noRole ? '#94a3b8' : 'var(--border)'}`,
                    background: filters.noRole ? '#f1f5f9' : 'transparent',
                    color: filters.noRole ? '#475569' : 'var(--text2)',
                    cursor: 'pointer', transition: 'all .12s',
                  }}
                >
                  No Role
                </button>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <Select
                  value={filters.status}
                  onChange={e => setFilter('status', e.target.value)}
                  style={{ width: 140 }}
                >
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="suspended">Suspended</option>
                </Select>
              </div>
            </div>
          </div>

          {/* Results summary + actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>
              {allLoading ? 'Loading…' : (
                <>
                  <strong style={{ color: 'var(--text)' }}>{filteredUsers.length}</strong>
                  {' '}{filteredUsers.length === 1 ? 'member' : 'members'} found
                  {hasActiveFilters && <span style={{ color: 'var(--text3)' }}> · filtered from {allUsers.length} total</span>}
                </>
              )}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {hasActiveFilters && (
                <button
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text3)', fontWeight: 500, padding: 0 }}
                >
                  Clear filters ×
                </button>
              )}
              <Btn variant="ghost" onClick={() => handleExport(filteredUsers)} disabled={exporting || allLoading || !filteredUsers.length}>
                {exporting ? <Spinner size={13} /> : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export Excel
                  </span>
                )}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <Card>
        <Table columns={columns} data={tableData} loading={tableLoad} />
        {activeRole === 'All' && (
          <Pagination page={page} pages={meta.pages} total={meta.total} limit={15} onChange={setPage} />
        )}
      </Card>

      {/* ── View User Modal ── */}
      {viewUser && (
        <UserViewModal
          user={viewUser}
          roles={roles}
          onClose={() => setViewUser(null)}
          onEdit={() => { setViewUser(null); setEditUser(viewUser); }}
          onEditRoles={() => { setViewUser(null); openRolesModal(viewUser); }}
          onStatusChange={(id, status) => updateStatus(id, status)}
        />
      )}

      {/* ── Edit Details Modal ── */}
      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSuccess={() => {
            setEditUser(null);
            reload();
            show('User details updated');
          }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {/* ── Status Modal ── */}
      {selected && (
        <Modal title={`Manage — ${selected.name}`} onClose={() => setSelected(null)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <Avatar name={selected.name} size={48} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{selected.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>{selected.email}</div>
              <div style={{ marginTop: 6 }}><Badge label={selected.status} type={selected.status} /></div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>Change account status:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.status !== 'active'    && <Btn variant="primary" onClick={() => updateStatus(selected.id, 'active')}>Activate</Btn>}
            {selected.status !== 'pending'   && <Btn variant="ghost"   onClick={() => updateStatus(selected.id, 'pending')}>Set pending</Btn>}
            {selected.status !== 'suspended' && <Btn variant="danger"  onClick={() => updateStatus(selected.id, 'suspended')}>Suspend</Btn>}
          </div>
        </Modal>
      )}

      {/* ── Roles Modal ── */}
      {rolesModal && (
        <Modal title={`Assign roles — ${rolesModal.name}`} onClose={() => setRolesModal(null)}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            Select one or more roles for this user.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {roles.filter(r => r.name !== 'Admin').map(r => (
              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(r.id)}
                  onChange={e => setSelectedRoles(prev =>
                    e.target.checked ? [...prev, r.id] : prev.filter(x => x !== r.id)
                  )}
                  style={{ accentColor: 'var(--accent)', width: 15, height: 15 }}
                />
                <span style={{ fontWeight: 500 }}>{r.name}</span>
                {r.description && <span style={{ color: 'var(--text3)', fontSize: 12 }}>— {r.description}</span>}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={saveRoles} disabled={saving}>
              {saving ? <Spinner size={14} color="#fff" /> : 'Save roles'}
            </Btn>
            <Btn variant="ghost" onClick={() => setRolesModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* ── Add User Modal ── */}
      {showAddUser && (
        <AddUserModal
          roles={roles}
          onClose={() => setShowAddUser(false)}
          onSuccess={(activateNow) => {
            setShowAddUser(false);
            reload();
            show(activateNow ? 'User created and activated successfully' : 'User created — status set to pending');
          }}
          onError={msg => show(msg, 'error')}
        />
      )}
    </div>
  );
}

// ── User View Modal ───────────────────────────────────────────────────────────
function UserViewModal({ user, onClose, onEdit, onEditRoles, onStatusChange }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    usersAPI.get(user.id)
      .then(r => setDetail(r.data.data))
      .catch(() => setDetail(user))
      .finally(() => setLoading(false));
  }, [user.id]);

  const data = detail || user;
  const roleList = parseRoles(data.roles).map(name => {
    const found = Array.isArray(data.roles) ? data.roles.find(r => r.name === name) : null;
    return found || { name };
  });

  return (
    <Modal title="" onClose={onClose} width={520}>
      <div style={{
        margin: '-20px -20px 20px', padding: '20px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface2)',
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <Avatar name={data.name} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 17 }}>{data.name}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{data.email}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <Badge label={data.status} type={data.status} />
            {roleList.map(r => <RolePill key={r.name} role={r.name} />)}
            {roleList.length === 0 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>No role assigned</span>}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text3)', cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>×</button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div>
            <SectionLabel>Contact information</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
              <MetaField label="Email"  value={data.email} />
              <MetaField label="Phone"  value={data.phone} />
              <MetaField label="Gender" value={data.gender} />
              <MetaField label="Joined" value={new Date(data.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })} />
            </div>
          </div>

          {/* Location */}
          <div>
            <SectionLabel>Location</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
              <MetaField label="Country"         value={data.country}/>
              <MetaField label="State / Province" value={data.state}/>
              <MetaField label="City"            value={data.city}/>
              <MetaField label="Area / Address"  value={data.area} wide/>
              <MetaField label="Mentor"          value={data.mentor} wide/>
            </div>
          </div>

          <div>
            <SectionLabel>Assigned roles</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {roleList.length === 0 ? (
                <div style={{
                  width: '100%', padding: '12px 14px',
                  background: 'var(--surface2)', borderRadius: 'var(--r)',
                  fontSize: 13, color: 'var(--text3)', fontStyle: 'italic',
                }}>
                  No roles assigned yet — click "Edit roles" to assign.
                </div>
              ) : roleList.map(r => (
                <div key={r.name} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'var(--accent-light)', border: '1px solid #b7e4c7',
                  borderRadius: 'var(--r)', padding: '7px 14px',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent2)' }}>{r.name}</span>
                  {r.description && <span style={{ fontSize: 11, color: 'var(--text3)' }}>— {r.description}</span>}
                </div>
              ))}
            </div>
          </div>

          <div>
            <SectionLabel>Account status</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
              <div style={{
                padding: '10px 16px', borderRadius: 'var(--r)',
                background: data.status === 'active' ? 'var(--accent-light)' : data.status === 'suspended' ? 'var(--red-bg)' : 'var(--gold-bg)',
                border: `1px solid ${data.status === 'active' ? '#b7e4c7' : data.status === 'suspended' ? '#f5c6c2' : '#f5e0a0'}`,
                fontSize: 13, fontWeight: 500,
                color: data.status === 'active' ? 'var(--accent2)' : data.status === 'suspended' ? 'var(--red)' : 'var(--gold)',
              }}>
                {data.status === 'active'    && '✓ Account is active'}
                {data.status === 'pending'   && '⏳ Awaiting activation'}
                {data.status === 'suspended' && '✗ Account suspended'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, paddingTop: 4, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={onEdit}>Edit details</Btn>
            <Btn variant="ghost" onClick={onEditRoles}>Edit roles</Btn>
            {data.status !== 'active'    && <Btn variant="primary" onClick={() => { onStatusChange(data.id, 'active');    onClose(); }}>Activate</Btn>}
            {data.status !== 'suspended' && <Btn variant="danger"  onClick={() => { onStatusChange(data.id, 'suspended'); onClose(); }}>Suspend</Btn>}
            {data.status !== 'pending'   && <Btn variant="ghost"   onClick={() => { onStatusChange(data.id, 'pending');   onClose(); }}>Set pending</Btn>}
          </div>

        </div>
      )}
    </Modal>
  );
}

// ── Edit User Modal ────────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onSuccess, onError }) {
  const [form, setForm] = useState({
    name:    user.name    || '',
    phone:   user.phone   || '',
    gender:  user.gender  || '',
    country: user.country || '',
    state:   user.state   || '',
    city:    user.city    || '',
    area:    user.area    || '',
    mentor:  user.mentor  || '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.gender) e.gender = 'Gender is required';
    if (form.phone && !/^\+?[\d\s\-()\\.]{7,15}$/.test(form.phone))
      e.phone = 'Enter a valid phone number';
    return e;
  }

  async function submit() {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      // Send only changed fields
      const patch = {};
      if (form.name    !== (user.name    || '')) patch.name    = form.name.trim();
      if (form.phone   !== (user.phone   || '')) patch.phone   = form.phone.trim()   || null;
      if (form.gender  !== (user.gender  || '')) patch.gender  = form.gender;
      if (form.country !== (user.country || '')) patch.country = form.country.trim() || null;
      if (form.state   !== (user.state   || '')) patch.state   = form.state.trim()   || null;
      if (form.city    !== (user.city    || '')) patch.city    = form.city.trim()    || null;
      if (form.area    !== (user.area    || '')) patch.area    = form.area.trim()    || null;
      if (form.mentor  !== (user.mentor  || '')) patch.mentor  = form.mentor.trim()  || null;

      if (!Object.keys(patch).length) { onClose(); return; }
      await usersAPI.update(user.id, patch);
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to update user');
    } finally { setSaving(false); }
  }

  function field(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: '' }));
  }

  return (
    <Modal title={`Edit — ${user.name}`} onClose={onClose} width={500}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        <SectionLabel>Personal details</SectionLabel>
        <Input label="Full name *" value={form.name} onChange={e => field('name', e.target.value)} error={errors.name} />
        <Input label="Phone number" type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={e => field('phone', e.target.value)} error={errors.phone} />
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Gender *</label>
          <Select value={form.gender} onChange={e => field('gender', e.target.value)} style={{ width: '100%' }}>
            <option value="">Select gender…</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </Select>
          {errors.gender && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>{errors.gender}</div>}
        </div>

        <SectionLabel>Location <span style={{ fontWeight: 400, letterSpacing: 0, fontSize: 10, textTransform: 'none' }}>(optional)</span></SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="Country" placeholder="e.g. India" value={form.country} onChange={e => field('country', e.target.value)} />
          <Input label="State / Province" placeholder="e.g. Kerala" value={form.state} onChange={e => field('state', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="City" placeholder="e.g. Kozhikode" value={form.city} onChange={e => field('city', e.target.value)} />
          <Input label="Area / Address" placeholder="e.g. Palayam" value={form.area} onChange={e => field('area', e.target.value)} />
        </div>
        <Input label="Mentor" placeholder="Name of mentor / sheikh" value={form.mentor} onChange={e => field('mentor', e.target.value)} />

        <div style={{ fontSize: 11, color: 'var(--text3)', background: 'var(--surface2)', borderRadius: 'var(--r)', padding: '8px 12px' }}>
          Email address cannot be changed here. Only fields that differ from current values will be saved.
        </div>

        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          <Btn onClick={submit} disabled={saving}>
            {saving ? <Spinner size={14} color="#fff" /> : 'Save changes'}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Add User Modal ─────────────────────────────────────────────────────────────
function AddUserModal({ roles, onClose, onSuccess, onError }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
    gender: '', country: '', state: '', city: '', area: '', mentor: '',
    roleIds: [], activateNow: true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  function validate() {
    const e = {};
    if (!form.name.trim())                       e.name = 'Full name is required';
    if (!form.email.trim())                      e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email))  e.email = 'Enter a valid email address';
    if (!form.gender)                            e.gender = 'Gender is required';
    if (!form.password)                          e.password = 'Password is required';
    else if (form.password.length < 8)           e.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword)  e.confirmPassword = 'Passwords do not match';
    if (form.phone && !/^\+?[\d\s\-()\\.]{7,15}$/.test(form.phone))
                                                 e.phone = 'Enter a valid phone number';
    return e;
  }

  async function submit() {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const { data: regData } = await usersAPI.create({
        name:     form.name.trim(),
        email:    form.email.trim().toLowerCase(),
        password: form.password,
        phone:    form.phone.trim()    || undefined,
        gender:   form.gender,
        country:  form.country.trim()  || undefined,
        state:    form.state.trim()    || undefined,
        city:     form.city.trim()     || undefined,
        area:     form.area.trim()     || undefined,
        mentor:   form.mentor.trim()   || undefined,
      });
      const userId = regData.data.id;
      if (form.activateNow) await usersAPI.updateStatus(userId, 'active');
      if (form.roleIds.length) await usersAPI.updateRoles(userId, form.roleIds);
      onSuccess(form.activateNow);
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to create user');
    } finally { setSaving(false); }
  }

  function field(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: '' }));
  }

  return (
    <Modal title="Add new user" onClose={onClose} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SectionLabel>Personal details</SectionLabel>
        <Input label="Full name *" placeholder="e.g. Ahmed Ali" value={form.name} onChange={e => field('name', e.target.value)} error={errors.name} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="Email address *" type="email" placeholder="ahmed@example.com" value={form.email} onChange={e => field('email', e.target.value)} error={errors.email} />
          <Input label="Phone number" type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={e => field('phone', e.target.value)} error={errors.phone} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 5, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Gender *</label>
          <Select value={form.gender} onChange={e => field('gender', e.target.value)} style={{ width: '100%' }}>
            <option value="">Select gender…</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </Select>
          {errors.gender && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>{errors.gender}</div>}
        </div>
        <SectionLabel>Location <span style={{ fontWeight: 400, letterSpacing: 0, fontSize: 10, textTransform: 'none' }}>(optional)</span></SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="Country" placeholder="e.g. India" value={form.country} onChange={e => field('country', e.target.value)} />
          <Input label="State / Province" placeholder="e.g. Kerala" value={form.state} onChange={e => field('state', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="City" placeholder="e.g. Kozhikode" value={form.city} onChange={e => field('city', e.target.value)} />
          <Input label="Area / Address" placeholder="e.g. Palayam" value={form.area} onChange={e => field('area', e.target.value)} />
        </div>
        <Input label="Mentor" placeholder="Name of mentor / sheikh" value={form.mentor} onChange={e => field('mentor', e.target.value)} />
        <SectionLabel>Set password</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="Password *" type="password" placeholder="Min. 8 characters" value={form.password} onChange={e => field('password', e.target.value)} error={errors.password} />
          <Input label="Confirm password *" type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => field('confirmPassword', e.target.value)} error={errors.confirmPassword} />
        </div>
        <SectionLabel>Assign roles</SectionLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {roles.filter(r => r.name !== 'Admin').map(r => {
            const checked = form.roleIds.includes(r.id);
            return (
              <label key={r.id} style={{
                display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer',
                background: checked ? 'var(--accent-light)' : 'var(--surface2)',
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--r)', padding: '6px 14px', transition: 'all .15s',
              }}>
                <input type="checkbox" style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                  checked={checked}
                  onChange={e => setForm(f => ({
                    ...f, roleIds: e.target.checked ? [...f.roleIds, r.id] : f.roleIds.filter(x => x !== r.id),
                  }))}
                />
                <span style={{ fontWeight: checked ? 500 : 400 }}>{r.name}</span>
              </label>
            );
          })}
        </div>
        <SectionLabel>Account options</SectionLabel>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" style={{ accentColor: 'var(--accent)', width: 15, height: 15, marginTop: 2, flexShrink: 0 }}
            checked={form.activateNow}
            onChange={e => setForm(f => ({ ...f, activateNow: e.target.checked }))}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Activate account immediately</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, lineHeight: 1.5 }}>
              Uncheck to create in "pending" status — the user cannot log in until activated.
            </div>
          </div>
        </label>
        <div style={{
          background: 'var(--accent-light)', border: '1px solid #b7e4c7',
          borderRadius: 'var(--r)', padding: '10px 14px', fontSize: 12, color: 'var(--accent2)', lineHeight: 1.6,
        }}>
          A welcome email will be sent automatically. Share the password separately through a secure channel.
        </div>
        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          <Btn onClick={submit} disabled={saving}>
            {saving ? <Spinner size={14} color="#fff" /> : 'Create user'}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
        {children}
      </span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}
